import { useState, useEffect, useCallback } from 'react';
import * as Cesium from 'cesium';
import { useEventSubscribe, eventBus } from '../events';
import { useProjectCreation } from '../contexts/ProjectCreationContext';
import { useUserProjectsContext } from '../contexts/UserProjectsContext';
import { useBilling } from '../contexts/BillingContext';
import { isLiveDemo } from '../utils/capabilities';
import { hasUnlimitedPlan } from '../utils/entitlements';
export function useDemUploadFlow(viewer, modalActions) {
    const { storePendingProject } = useProjectCreation();
    const { projects } = useUserProjectsContext();
    // An active unlimited plan creates unfettered — the one-active-demo gate
    // below never applies.
    const { entitlements } = useBilling();
    const unlimited = hasUnlimitedPlan(entitlements);
    const [isDemUploading, setIsDemUploading] = useState(false);
    const [preDemCameraPosition, setPreDemCameraPosition] = useState(null);
    const [initialDemData, setInitialDemData] = useState(null);
    // End of the DEM upload flow — mirrors DrawingOrchestrator.handleDrawComplete
    const handleDemUploadComplete = useCallback((payload) => {
        var _a;
        // Extract coordinates from GeoJSON for pending project storage
        let coordinates = [];
        try {
            const geoJson = JSON.parse(payload.requestedBoundsGeojson);
            if (geoJson.type === 'Polygon' && ((_a = geoJson.coordinates) === null || _a === void 0 ? void 0 : _a[0])) {
                coordinates = geoJson.coordinates[0].map((coord) => ({
                    lat: coord[1],
                    lng: coord[0]
                }));
            }
        }
        catch (_b) { }
        // Stash the (already-created) DEM project as pending. demProjectId marks it
        // so the confirmation starts its demo render instead of creating a new
        // project — and the render only fires on confirm, matching the polygon flow.
        storePendingProject({
            projectName: payload.projectName,
            projectDescription: payload.projectDescription,
            coordinates,
            bufferDistanceMeters: 0,
            areaUnit: 'acres',
            projectAreaAcres: payload.requestedAreaAcres,
            standardContourUnit: payload.standardContourUnit,
            customContours: payload.customContours,
            demProjectId: payload.projectId,
        });
        // One-active-demo gate (mirrors DrawingOrchestrator.handleDrawComplete): if the
        // user already has an active high-res demo, offer to unlock it first instead
        // of silently starting another. Owned projects only — a demo shared with
        // you never occupies your slot — and an unlimited plan skips the gate.
        const nowSec = Date.now() / 1000;
        const activeDemo = unlimited ? undefined : projects.find((p) => p.isOwner !== false &&
            isLiveDemo(p.accessLevel) &&
            p.demoExpiresAt &&
            nowSec < p.demoExpiresAt);
        if (activeDemo) {
            eventBus.emit('demo:promptUnlockBeforeCreate', {
                activeDemoId: activeDemo.id,
                activeDemoName: activeDemo.name,
                activeDemoExpiresAt: activeDemo.demoExpiresAt,
                pendingProjectName: payload.projectName,
            });
            return;
        }
        // Otherwise show the same "start your 7-day demo" confirmation the polygon
        // flow uses. Confirming there starts the render (via demProjectId).
        eventBus.emit('project:confirmCreate', { projectName: payload.projectName });
    }, [storePendingProject, projects, unlimited]);
    useEventSubscribe('dem:backToEdit', (payload) => {
        setInitialDemData({
            projectId: payload.projectId,
            projectName: payload.projectName,
            projectDescription: payload.projectDescription,
            requestedBoundsGeojson: payload.requestedBoundsGeojson,
            requestedAreaAcres: payload.requestedAreaAcres,
            standardContourUnit: payload.standardContourUnit,
            customContours: payload.customContours
        });
        modalActions.openCreateProject();
    }, []);
    useEventSubscribe('dem:uploadStart', () => {
        if (viewer && !viewer.isDestroyed()) {
            const camera = viewer.camera;
            setPreDemCameraPosition({
                destination: camera.position.clone(),
                orientation: {
                    heading: camera.heading,
                    pitch: camera.pitch,
                    roll: camera.roll
                }
            });
        }
        setIsDemUploading(true);
    }, [viewer]);
    useEventSubscribe('dem:uploadComplete', () => {
        setIsDemUploading(false);
        setPreDemCameraPosition(null);
    }, []);
    useEventSubscribe('dem:uploadError', () => {
        setIsDemUploading(false);
        if (viewer && !viewer.isDestroyed() && preDemCameraPosition) {
            viewer.camera.setView({
                destination: preDemCameraPosition.destination,
                orientation: preDemCameraPosition.orientation
            });
        }
        setPreDemCameraPosition(null);
    }, [viewer, preDemCameraPosition]);
    useEventSubscribe('dem:projectConfigured', (payload) => {
        var _a;
        // Fly camera to project bounds
        if (viewer && !viewer.isDestroyed() && payload.requestedBoundsGeojson) {
            try {
                const geoJson = JSON.parse(payload.requestedBoundsGeojson);
                if (geoJson.type === 'Polygon' && ((_a = geoJson.coordinates) === null || _a === void 0 ? void 0 : _a[0])) {
                    let minLng = Infinity, maxLng = -Infinity;
                    let minLat = Infinity, maxLat = -Infinity;
                    geoJson.coordinates[0].forEach((coord) => {
                        const [lng, lat] = coord;
                        minLng = Math.min(minLng, lng);
                        maxLng = Math.max(maxLng, lng);
                        minLat = Math.min(minLat, lat);
                        maxLat = Math.max(maxLat, lat);
                    });
                    const centerLng = (minLng + maxLng) / 2;
                    const centerLat = (minLat + maxLat) / 2;
                    const latRange = maxLat - minLat;
                    const lngRange = maxLng - minLng;
                    const maxRange = Math.max(latRange, lngRange);
                    let altitude = 50000;
                    if (maxRange > 0.1)
                        altitude = 200000;
                    else if (maxRange > 0.05)
                        altitude = 100000;
                    else if (maxRange > 0.01)
                        altitude = 75000;
                    viewer.camera.flyTo({
                        destination: Cesium.Cartesian3.fromDegrees(centerLng, centerLat, altitude),
                        orientation: {
                            heading: Cesium.Math.toRadians(0.0),
                            pitch: Cesium.Math.toRadians(-90.0),
                            roll: 0.0
                        },
                        duration: 2.5
                    });
                }
            }
            catch (error) {
                console.error('Failed to parse DEM project bounds for camera positioning:', error);
            }
        }
        // Route into the create confirmation
        handleDemUploadComplete(payload);
    }, [viewer, handleDemUploadComplete]);
    // Slow globe rotation during DEM upload
    useEffect(() => {
        if (!viewer || !isDemUploading)
            return;
        let preRenderListener;
        let lastTime = Date.now();
        let hasZoomedOut = false;
        let startSpinningTime = 0;
        if (isDemUploading && !hasZoomedOut) {
            hasZoomedOut = true;
            viewer.camera.flyTo({
                destination: Cesium.Cartesian3.fromDegrees(0.0, 0.0, 12000000),
                orientation: {
                    heading: Cesium.Math.toRadians(0.0),
                    pitch: Cesium.Math.toRadians(-90.0),
                    roll: 0.0
                },
                duration: 1.0,
                complete: () => {
                    startSpinningTime = Date.now();
                    lastTime = startSpinningTime;
                }
            });
            startSpinningTime = Date.now() + 1000;
        }
        const spinGlobe = () => {
            const spinRate = 0.02;
            preRenderListener = () => {
                if (!isDemUploading || !viewer || viewer.isDestroyed()) {
                    return;
                }
                const currentTime = Date.now();
                if (isDemUploading && startSpinningTime > 0 && currentTime < startSpinningTime) {
                    return;
                }
                const deltaTime = (currentTime - lastTime) / 1000;
                lastTime = currentTime;
                const angle = -spinRate * deltaTime;
                viewer.scene.camera.rotate(Cesium.Cartesian3.UNIT_Z, angle);
            };
            viewer.scene.preRender.addEventListener(preRenderListener);
        };
        const controller = viewer.scene.screenSpaceCameraController;
        const originalSettings = {
            enableRotate: controller.enableRotate,
            enableTranslate: controller.enableTranslate,
            enableZoom: controller.enableZoom,
            enableTilt: controller.enableTilt,
            enableLook: controller.enableLook
        };
        controller.enableRotate = false;
        controller.enableTranslate = false;
        controller.enableZoom = false;
        controller.enableTilt = false;
        controller.enableLook = false;
        spinGlobe();
        return () => {
            if (preRenderListener && viewer && !viewer.isDestroyed()) {
                viewer.scene.preRender.removeEventListener(preRenderListener);
            }
            if (viewer && !viewer.isDestroyed()) {
                const controller = viewer.scene.screenSpaceCameraController;
                controller.enableRotate = originalSettings.enableRotate;
                controller.enableTranslate = originalSettings.enableTranslate;
                controller.enableZoom = originalSettings.enableZoom;
                controller.enableTilt = originalSettings.enableTilt;
                controller.enableLook = originalSettings.enableLook;
            }
        };
    }, [viewer, isDemUploading]);
    return {
        isDemUploading,
        initialDemData,
        setInitialDemData,
    };
}
